import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import { clinicalApi, type CurrentUser, type PdcaTaskRecord } from "../../lib/api/clinical";
import { formField } from "./format";

const PHASE_LABELS: Record<string, string> = {
  plan: "计划(P)",
  do: "执行(D)",
  check: "检查(C)",
  act: "处置(A)",
};
const NEXT_PHASE: Record<string, string> = { plan: "do", do: "check", check: "act", act: "act" };

interface Props {
  user: CurrentUser;
}

export function PdcaPanel({ user }: Props) {
  const [tasks, setTasks] = useState<PdcaTaskRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const canWrite = user.role === "admin" || user.role === "infection_control";

  const load = useCallback(async () => {
    const data = await clinicalApi.pdcaTasks();
    setTasks(data);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load().catch(() => {
      setMessage("加载 PDCA 任务失败");
    });
  }, [load]);

  async function run(task: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMessage("");
    try {
      await task();
      await load();
      setMessage(ok);
    } catch {
      setMessage("操作失败");
    } finally {
      setBusy(false);
    }
  }

  async function create(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const title = formField(form, "title").trim();
    const owner = formField(form, "owner").trim();
    if (!title || !owner) {
      setMessage("请填写任务名称与负责人");
      return;
    }
    const target = formField(form, "target").trim();
    event.currentTarget.reset();
    await run(
      () => clinicalApi.createPdcaTask({ title, owner_ref: owner, target: target || undefined }),
      "已创建任务",
    );
  }

  return (
    <div className="view">
      <div className="section-head">
        <h2>PDCA 持续改进</h2>
        {message ? <span role="status">{message}</span> : null}
      </div>

      {canWrite ? (
        <form className="pdca-form" onSubmit={(event) => void create(event)}>
          <input name="title" placeholder="改进任务名称" />
          <input name="owner" placeholder="负责人/科室" />
          <input name="target" placeholder="目标（可选）" />
          <button type="submit" className="primary" disabled={busy}>
            新建任务
          </button>
        </form>
      ) : null}

      {tasks.length === 0 ? (
        <p>暂无改进任务。</p>
      ) : (
        <table className="queue-table">
          <thead>
            <tr>
              <th>任务</th>
              <th>负责人</th>
              <th>目标</th>
              <th>阶段</th>
              <th>进度</th>
              <th>状态</th>
              {canWrite ? <th>操作</th> : null}
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.id}>
                <td>{task.title}</td>
                <td>{task.owner_ref}</td>
                <td>{task.target ?? "—"}</td>
                <td>{PHASE_LABELS[task.phase] ?? task.phase}</td>
                <td>{task.progress}%</td>
                <td>{task.status}</td>
                {canWrite ? (
                  <td className="row-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        void run(
                          () =>
                            clinicalApi.updatePdcaTask(task.id, task.version, {
                              phase: NEXT_PHASE[task.phase],
                              progress: Math.min(100, task.progress + 25),
                            }),
                          "已推进阶段",
                        );
                      }}
                    >
                      推进
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        void run(
                          () =>
                            clinicalApi.updatePdcaTask(task.id, task.version, {
                              status: "closed",
                              progress: 100,
                            }),
                          "已关闭任务",
                        );
                      }}
                    >
                      关闭
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
